import React from 'react';
import { DragDropProvider } from '@dnd-kit/react';
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { move } from '@dnd-kit/helpers';
import _min from 'lodash/min';
import _max from 'lodash/max';
import _cloneDeep from 'lodash/cloneDeep';
import { MetricGroups, useExperimentsStore } from 'apps/experiments/src/store';
import { ExperimentMetricType } from 'apps/experiments/src/types';
import { useFlattenedMetricResults, useVariants } from 'apps/experiments/src/components/hooks/metric-results';
import { useExperimentMetrics } from 'iron/apps/experiments/src/components/hooks/metrics';
import styles from './index.module.styl';
import { ExperimentPageSummaryMetricsCategory } from './_category';
import { ExperimentPageSummaryMetricsModal } from './_metric-modal';
export function ExperimentPageSummaryMetrics() {
  var _draftMetricGroups$Ex, _draftMetricGroups$Ex2;
  const experimentState = useExperimentsStore(state => state.experimentState);
  const currentMetric = useExperimentsStore(state => state.currentMetric);
  const metricResults = useExperimentsStore(state => state.metricResults);
  const {
    metrics,
    reorderMetrics
  } = useExperimentMetrics();
  const flattenedMetricResults = useFlattenedMetricResults();
  const variants = useVariants();
  const sortedVariants = React.useMemo(() => Array.from(variants).sort((a, b) => {
    return a.localeCompare(b);
  }), [variants]);

  // this is used to compute the chart header/row possible range
  const min = React.useMemo(() => Math.min(0, _min(flattenedMetricResults.map(result => result.lift - result.liftConfidence))!), [flattenedMetricResults]);
  const max = React.useMemo(() => Math.max(0, _max(flattenedMetricResults.map(result => result.lift + result.liftConfidence))!), [flattenedMetricResults]);
  const absMax = flattenedMetricResults.length ? Math.max(Math.abs(min), Math.abs(max)) * 1.1 : 0.5;

  // we need this map to contain keys for all possible metric types for drag and drop
  // to work correctly when moving a metric into an empty group
  const metricGroups = React.useMemo(() => metrics.reduce<MetricGroups>((groups, metric) => {
    groups[metric.type].push(metric);
    return groups;
  }, {
    [ExperimentMetricType.PRIMARY]: [],
    [ExperimentMetricType.SECONDARY]: []
  }), [metrics]);

  // this draft state is used for drag and drop
  // if a drag is canceled, then we can easily revert to the original state
  const [draftMetricGroups, setDraftMetricGroups] = React.useState(_cloneDeep(metricGroups));
  React.useEffect(() => {
    setDraftMetricGroups(_cloneDeep(metricGroups));
  }, [metricGroups]);
  if (!experimentState) {
    return null;
  }
  return <div className={styles.metrics} data-sentry-component="ExperimentPageSummaryMetrics" data-sentry-source-file="index.tsx">
      <DragDropProvider
    // @ts-ignore
    modifiers={[RestrictToVerticalAxis]} onDragOver={event => {
      setDraftMetricGroups(prev => move(prev, event));
    }} onDragEnd={event => {
      if (event.canceled) {
        setDraftMetricGroups(_cloneDeep(metricGroups));
        return;
      }
      reorderMetrics(draftMetricGroups);
    }} data-sentry-element="DragDropProvider" data-sentry-source-file="index.tsx">
        <ExperimentPageSummaryMetricsCategory absMax={absMax} header='experiments.main.metrics.primary' metrics={(_draftMetricGroups$Ex = draftMetricGroups[ExperimentMetricType.PRIMARY]) !== null && _draftMetricGroups$Ex !== void 0 ? _draftMetricGroups$Ex : []} variants={sortedVariants} type={ExperimentMetricType.PRIMARY} results={metricResults} data-sentry-element="ExperimentPageSummaryMetricsCategory" data-sentry-source-file="index.tsx" />
        <ExperimentPageSummaryMetricsCategory absMax={absMax} header='experiments.main.metrics.secondary' metrics={(_draftMetricGroups$Ex2 = draftMetricGroups[ExperimentMetricType.SECONDARY]) !== null && _draftMetricGroups$Ex2 !== void 0 ? _draftMetricGroups$Ex2 : []} variants={sortedVariants} type={ExperimentMetricType.SECONDARY} results={metricResults} data-sentry-element="ExperimentPageSummaryMetricsCategory" data-sentry-source-file="index.tsx" />
      </DragDropProvider>
      <ExperimentPageSummaryMetricsModal metric={currentMetric} data-sentry-element="ExperimentPageSummaryMetricsModal" data-sentry-source-file="index.tsx" />
    </div>;
}